/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@coreai/component-library';
import { externalSignin } from 'services/userServices';
import { useLanguage } from 'LanguageContext';

const SignInForm = () => {
  const theme = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { t } = useLanguage();

  const containerStyle = css`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f3f4f6;
    padding: 1rem;
  `;

  const formStyle = css`
    background: white;
    padding: 2.5rem;
    border-radius: 0.5rem;
    box-shadow: 0px 4px 24px 0px #0000001A;
    width: 100%;
    max-width: 450px;

    @media (max-width: 768px) {
      padding: 1.5rem;
    }
  `;

  const headingStyle = css`
    font-size: 30px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0%;
    margin-bottom: 1.25rem;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  `;

  const labelGroup = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
  `;

  const labelStyle = css`
    font-size: 0.875rem;
    font-weight: 500;
  `;

  const forgotLink = css`
    font-size: 0.875rem;
    color: ${theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
    
    @media (max-width: 768px) {
      font-size: 12px;
    }
  `;

  const inputStyle = css`
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #acb8bd;
    border-radius: 0.5rem;
    margin-bottom: 1.25rem;
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}33;
    }
  `;

  const buttonStyle = css`
    width: 100%;
    background-color: ${theme.colors.primary};
    color: white;
    padding: 0.5rem;
    border: none;
    border-radius: 1.25rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
  `;

  const handleSubmit = (e: any) => {
    e.preventDefault();
  
    const formData = {
      email,
      password,
    };
  
    externalSignin(formData)
      .then((result: any) => {
        if (result.success) {
          toast({
            variant: 'success',
            title: result?.message,
          });
          navigate(`/`);
        } else {
          // Handle login failure
          setEmail('');
          setPassword('');
          toast({
            variant: 'error',
            title: result?.message || 'Login failed. Please try again.',
          });
        }
      })
      .catch((error: any) => {
        console.log({ error }, 'updateUserConsent');
        toast({
          variant: 'error',
          title: error?.response?.data?.message || 'An error occurred during login.',
        });
      });
  };  

  return (
    <div css={containerStyle}>
      <div css={formStyle}>
        <form onSubmit={handleSubmit}>
          <h2 css={headingStyle}>{t("signin_title")}</h2>

          <div>
            <label css={labelStyle}>{t("signin_email")}</label>
            <input
              type="email"
              placeholder={t('signin_enter_email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              css={inputStyle}
              required
            />
          </div>

          <div>
            <div css={labelGroup}>
              <label css={labelStyle}>{t("signin_password")}</label>
              <a href="/password?type=forgot" css={forgotLink}>{t("signin_forgot_password_link")}</a>
            </div>
            <input
              type="password"
              placeholder={t('signin_enter_password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              css={inputStyle}
              required
            />
          </div>

          <button type="submit" css={buttonStyle}>
          {t("signin_btn")}
          </button>

        </form>

        <div style={{textAlign: 'center', padding: '12px 0'}}>{t("signin_or")}</div>

        <button css={buttonStyle} onClick={() => window.open('/api/app/sso/oic-login',"_self")}>
          SSO
        </button>
      </div>
    </div>
  );
};

export default SignInForm;